import React, { useEffect, useState, useCallback } from 'react';
import { Check, X, BellRinging } from '@phosphor-icons/react';
import { toast } from 'sonner';

export const ApprovalsTab = ({ api, onChanged }) => {
  const [data, setData] = useState({ task_logs: [], redemptions: [] });
  const [loading, setLoading] = useState(true);
  const [approvingAll, setApprovingAll] = useState(false);

  const fetchData = useCallback(async () => {
    try { const r = await api.get('/kids/approvals'); setData(r.data); } catch (e) { console.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const act = async (path, msg) => {
    try {
      const r = await api.post(path);
      toast.success(msg + (r.data.points ? ` +${r.data.points} pts` : ''));
      if (r.data.streak_bonus) toast.success(`🔥 ${r.data.streak_bonus.streak}-day streak! Bonus +${r.data.streak_bonus.bonus} pts!`);
      fetchData(); onChanged();
    }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const approveAll = async () => {
    setApprovingAll(true);
    try {
      const r = await api.post('/kids/approvals/approve-all');
      toast.success(`Approved ${r.data.approved} task(s) — +${r.data.total_points} points credited`);
      (r.data.streak_bonuses || []).forEach(b => toast.success(`🔥 ${b.streak}-day streak! Bonus +${b.bonus}`));
      fetchData(); onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setApprovingAll(false);
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const empty = data.task_logs.length === 0 && data.redemptions.length === 0;

  return (
    <div className="space-y-5" data-testid="kids-approvals-tab">
      {empty && (
        <div className="glass-card p-10 text-center">
          <BellRinging size={40} className="mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">All caught up! Nothing waiting for approval.</p>
        </div>
      )}

      {data.task_logs.length > 0 && (
        <div className="glass-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm">Tasks marked done by kids ({data.task_logs.length})</h3>
            <button onClick={approveAll} disabled={approvingAll} data-testid="approve-all-btn"
              className="px-3 py-1.5 rounded-full bg-green-600 text-white text-xs font-semibold flex items-center gap-1 disabled:opacity-50">
              <Check size={12} weight="bold" /> {approvingAll ? 'Approving…' : 'Approve All'}
            </button>
          </div>
          <div className="space-y-2">
            {data.task_logs.map(l => (
              <div key={l.log_id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/40" data-testid={`approval-task-${l.log_id}`}>
                <span className="text-xl">{l.kid_emoji}</span>
                <div className="flex-1">
                  <p className="text-sm font-medium">{l.emoji || '⭐'} {l.title}</p>
                  <p className="text-xs text-muted-foreground">{l.kid_name} · {l.date} · <span className="text-amber-600 font-semibold">+{l.points} pts</span></p>
                </div>
                <button onClick={() => act(`/kids/task-log/${l.log_id}/approve`, 'Approved!')} data-testid={`approve-task-${l.log_id}`}
                  className="px-3 py-1.5 rounded-full bg-green-600 text-white text-xs font-semibold flex items-center gap-1"><Check size={12} weight="bold" /> Approve</button>
                <button onClick={() => act(`/kids/task-log/${l.log_id}/reject`, 'Rejected')} data-testid={`reject-task-${l.log_id}`}
                  className="px-3 py-1.5 rounded-full border border-red-500/40 text-red-500 text-xs font-semibold flex items-center gap-1"><X size={12} weight="bold" /> Reject</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.redemptions.length > 0 && (
        <div className="glass-card p-5">
          <h3 className="font-semibold text-sm mb-3">Reward requests ({data.redemptions.length})</h3>
          <div className="space-y-2">
            {data.redemptions.map(r => (
              <div key={r.redemption_id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/40" data-testid={`approval-redemption-${r.redemption_id}`}>
                <span className="text-xl">{r.kid_emoji}</span>
                <div className="flex-1">
                  <p className="text-sm font-medium">{r.reward_emoji} {r.reward_title}</p>
                  <p className="text-xs text-muted-foreground">{r.kid_name} wants to spend <span className="text-amber-600 font-semibold">⭐ {r.cost}</span></p>
                </div>
                <button onClick={() => act(`/kids/redemptions/${r.redemption_id}/approve`, 'Reward granted!')} data-testid={`approve-redemption-${r.redemption_id}`}
                  className="px-3 py-1.5 rounded-full bg-green-600 text-white text-xs font-semibold flex items-center gap-1"><Check size={12} weight="bold" /> Grant</button>
                <button onClick={() => act(`/kids/redemptions/${r.redemption_id}/reject`, 'Declined')} data-testid={`reject-redemption-${r.redemption_id}`}
                  className="px-3 py-1.5 rounded-full border border-red-500/40 text-red-500 text-xs font-semibold flex items-center gap-1"><X size={12} weight="bold" /> Decline</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
